import {
CopyOutlined,
DeleteOutlined,
DownloadOutlined,
EditOutlined,
EyeOutlined,
InfoCircleOutlined,
FolderOpenOutlined,
LinkOutlined,
} from "@ant-design/icons";
import type { ListColumn } from "../list/listColumn";
import { Button,Space, type ButtonProps } from "../ui";
import React from "react";
import type { LocalizedText } from "./settingsListTypes";
import { confirmAction,type ConfirmActionOptions,type ConfirmModalApi,type TextResolver } from "./confirmAction";

/**
 * Gewicht einer Zeilenaktion, nicht ihre Farbe: „link" ist die Hauptaktion, „default" das
 * Nebengeräusch (Navigation, Ansehen), „warning" der Eingriff in einen laufenden Vorgang,
 * „danger" das Zurücknehmen oder Löschen. Ohne diese Abstufung liest sich jede Zeile so, als
 * wäre jede Aktion gleich wichtig.
 */
export type ListActionKind = "default" | "primary" | "danger" | "link" | "warning";

/** Canonical native action button, also used by product adapters with existing action handlers. */
export function renderListActionButton({ kind = "link", size = "small", danger, style, key, className, ...props }: Omit<ButtonProps, "type"> & { kind?: ListActionKind; key?: React.Key }): React.ReactElement {
  // `key` gehört an das Element, nicht in die gestreuten Props (React-Warnung).
  return <Button key={key} {...props} size={size} type={kind === "primary" ? "primary" : kind === "default" ? "text" : "link"}
    className={kind === "warning" ? [className, "list-action--warning"].filter(Boolean).join(" ") : className}
    danger={danger || kind === "danger"} style={kind === "warning" ? { color: "var(--ant-color-warning)", ...style } : style} />;
}

export function ListActionButton(props: Omit<ButtonProps, "type"> & { kind?: ListActionKind }) {
  return renderListActionButton(props);
}
// Reiner Darstellungsbaustein: Listen lösen ihn für Aktionsmessung und Nur-Symbol-Darstellung auf.
ListActionButton.settingsListRole = "render" as const;
export type ListActionPreset = "edit" | "duplicate" | "delete" | "view" | "details" | "open" | "download" | "copy";

export type ListActionConfirm<T> = Omit<ConfirmActionOptions, "onConfirm"> & {
  onConfirm?: (record: T) => Promise<void> | void;
};

export type ListActionDefinition<T> = {
  key: React.Key;
  label: LocalizedText | ((record: T) => LocalizedText);
  icon?: React.ReactNode | ((record: T) => React.ReactNode);
  kind?: ListActionKind;
  visible?: boolean | ((record: T) => boolean);
  disabled?: boolean | ((record: T) => boolean);
  loading?: boolean | ((record: T) => boolean);
  onClick?: (record: T, event: React.MouseEvent<HTMLElement>) => Promise<void> | void;
  confirm?: ListActionConfirm<T> | ((record: T) => ListActionConfirm<T>);
  /**
   * Klick auf die Zeile führt diese Aktion aus, ihr Knopf entfällt. Standard für `edit`;
   * `false` behält den Bearbeiten-Knopf, `true` macht eine andere Hauptaktion (z. B. „fortsetzen“) zum Zeilenklick.
   */
  rowClick?: boolean;
  /**
   * Zeilenklick, solange die Aktion für diese Zeile ausgeblendet oder gesperrt ist: öffnet dieselbe Ansicht
   * nur zum Ansehen (z. B. `FormDrawer readOnly`), statt dass der Klick ins Leere geht.
   */
  onView?: (record: T) => void;
  /**
   * Escape hatch fuer fachliche Sonderfaelle. Standardaktionen sollen Button/Icon/Confirm
   * verwenden, damit SettingsListPage Layout, Mobile und Icon-only zentral steuern kann.
   */
  render?: (record: T) => React.ReactNode;
};

export type ListActionResolver<T> = (record: T, actions: ListActionDefinition<T>[]) => ListActionDefinition<T>[];

export type ListActionColumnOptions = {
  key?: string;
  title?: React.ReactNode;
  width?: number;
  slotCount?: number;
};

export function listActionPreset<T>(
  preset: ListActionPreset,
  overrides: Omit<ListActionDefinition<T>, "key" | "label" | "icon" | "kind"> & Partial<Pick<ListActionDefinition<T>, "key" | "label" | "icon" | "kind">>
): ListActionDefinition<T> {
  const defaults: Record<ListActionPreset, Pick<ListActionDefinition<T>, "key" | "label" | "icon" | "kind">> = {
    edit: { key: "edit", label: "Edit", icon: <EditOutlined />, kind: "link" },
    duplicate: { key: "duplicate", label: "Duplicate", icon: <CopyOutlined />, kind: "link" },
    delete: { key: "delete", label: "Delete", icon: <DeleteOutlined />, kind: "danger" },
    view: { key: "view", label: "View", icon: <EyeOutlined />, kind: "link" },
    // Öffnet eine Ansicht; `⋯` stünde für ein Menü.
    details: { key: "details", label: "Details", icon: <InfoCircleOutlined />, kind: "link" },
    open: { key: "open", label: "Open", icon: <FolderOpenOutlined />, kind: "link" },
    download: { key: "download", label: "Download", icon: <DownloadOutlined />, kind: "link" },
    copy: { key: "copy", label: "Copy", icon: <LinkOutlined />, kind: "link" },
  };
  const cleanOverrides = Object.fromEntries(Object.entries(overrides).filter(([, value]) => value !== undefined));
  return { ...defaults[preset], ...cleanOverrides };
}

export function isListActionVisible<T>(action: ListActionDefinition<T>, record: T): boolean {
  return typeof action.visible === "function" ? action.visible(record) : action.visible ?? true;
}

export function renderListAction<T>(
  action: ListActionDefinition<T>,
  record: T,
  text: TextResolver,
  modal?: ConfirmModalApi
): React.ReactNode {
  if (action.render) return action.render(record);

  const disabled = typeof action.disabled === "function" ? action.disabled(record) : action.disabled ?? false;
  const loading = typeof action.loading === "function" ? action.loading(record) : action.loading ?? false;
  const icon = typeof action.icon === "function" ? action.icon(record) : action.icon;
  const label = typeof action.label === "function" ? action.label(record) : action.label;
  const kind = action.kind ?? "link";
  const confirm = typeof action.confirm === "function" ? action.confirm(record) : action.confirm;

  return renderListActionButton({
      key: action.key,
      kind,
      icon,
      disabled,
      loading,
      onClick: (event) => {
        event.stopPropagation();
        if (confirm && modal) {
          confirmAction(modal, text, {
            ...confirm,
            danger: confirm.danger ?? kind === "danger",
            onConfirm: () => confirm.onConfirm?.(record) ?? action.onClick?.(record, event),
          });
          return;
        }
        return action.onClick?.(record, event);
      },
      children: text(label),
  });
}

export function ListActions<T>({
  actions,
  record,
  text,
  modal,
}: {
  actions: ListActionDefinition<T>[];
  record: T;
  text: TextResolver;
  modal?: ConfirmModalApi;
}) {
  return (
    <Space size="small" wrap={false}>
      {actions
        .filter((action) => isListActionVisible(action, record))
        .map((action) => (
          <React.Fragment key={action.key}>{renderListAction(action, record, text, modal)}</React.Fragment>
        ))}
    </Space>
  );
}

export function resolveListActions<T>(
  actions: ListActionDefinition<T>[],
  record: T,
  resolveActions?: ListActionResolver<T>
): ListActionDefinition<T>[] {
  return resolveActions ? resolveActions(record, actions) : actions;
}

export function makeActionColumn<T>({
  key = "actions",
  title = "",
  width = 200,
  slotCount,
}: ListActionColumnOptions = {}): ListColumn<T> & { slotCount?: number } {
  return {
    title,
    key,
    valueType: "option",
    width,
    slotCount,
  };
}

export function listActionsToSlots<T>(
  actions: ListActionDefinition<T>[],
  text: TextResolver,
  modal?: ConfirmModalApi,
  resolveActions?: ListActionResolver<T>
): Array<{ show: (record: T) => boolean; render: (record: T) => React.ReactNode }> {
  return actions.map((_, index) => ({
    show: (record) => {
      const resolved = resolveListActions(actions, record, resolveActions);
      const action = resolved[index];
      return action ? isListActionVisible(action, record) : false;
    },
    render: (record) => {
      const resolved = resolveListActions(actions, record, resolveActions);
      const action = resolved[index];
      return action ? renderListAction(action, record, text, modal) : null;
    },
  }));
}
